// features/workstations/close/script-builder.js
// Build mode — inputs → deterministic 12-step consultative script.

import { useState } from 'react';
import ScriptDisplay from './script-display';

const labelStyle = { display: 'block', fontSize: 12, color: '#9a9aa6', marginBottom: 5 };
const inputStyle = { width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border-2)', borderRadius: 8, padding: '10px 12px', color: 'inherit', fontSize: 14, fontFamily: 'inherit' };
const card = { background: 'var(--surface-2)', border: '1px solid var(--border-3)', borderRadius: 12, padding: 20, marginBottom: 16 };

export default function ScriptBuilder({ workstation, userId, onSaved }) {
  const [product, setProduct] = useState('');
  const [prospect, setProspect] = useState('');
  const [price, setPrice] = useState('');
  const [problemArea, setProblemArea] = useState('');
  const [features, setFeatures] = useState('');
  const [busy, setBusy] = useState(false);
  const [script, setScript] = useState(null);
  const [error, setError] = useState(null);

  const canBuild = product.trim().length > 1 && !busy;

  const build = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch('/api/workstations/close', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'build', user_id: userId, workstation_id: workstation?.id,
          product, prospect, price, problem_area: problemArea, features,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setScript(data.script);
      onSaved?.();
    } catch (e) {
      setError(e?.message || 'Build failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Build a consultative script</div>
        <div style={{ fontSize: 13, color: '#9a9aa6', marginBottom: 16 }}>Diagnose before you prescribe — Close generates the 12-step script tailored to what you sell.</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>What are you selling? *</label><input style={inputStyle} value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Turnkey make-ready service" /></div>
          <div><label style={labelStyle}>Prospect</label><input style={inputStyle} value={prospect} onChange={(e) => setProspect(e.target.value)} placeholder="Small landlord (5–30 units)" /></div>
          <div><label style={labelStyle}>Price</label><input style={inputStyle} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$600 / turnover" /></div>
          <div><label style={labelStyle}>Problem area (for the discovery question)</label><input style={inputStyle} value={problemArea} onChange={(e) => setProblemArea(e.target.value)} placeholder="turning over a unit" /></div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Key features (comma-separated)</label>
          <input style={inputStyle} value={features} onChange={(e) => setFeatures(e.target.value)} placeholder="one call for everything, photo-confirmed, on-deadline, we maintain after" />
        </div>
        {error && <div style={{ color: '#c46050', fontSize: 13, marginTop: 10 }}>{error}</div>}
        <button onClick={build} disabled={!canBuild}
          style={{ marginTop: 14, width: '100%', padding: 12, borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 14, cursor: canBuild ? 'pointer' : 'not-allowed', background: canBuild ? '#c8a96e' : 'var(--surface-4)', color: canBuild ? '#1a1a1a' : '#666' }}>
          {busy ? 'Generating…' : '⚡ Generate consultative script'}
        </button>
      </div>

      {script && (
        <div style={card}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', color: '#9a9aa6', marginBottom: 12 }}>Your script — 12-step consultative method</div>
          <ScriptDisplay script={script} />
          <div style={{ fontSize: 11, color: '#6a6a76', marginTop: 12 }}>Education + honest-selling guardrails: surfaces real pain, never fabricates urgency. Tweak the wording to your voice.</div>
        </div>
      )}
    </div>
  );
}
